import './EditorHeader.css';

import { FC, useState } from 'react';
import { IonIcon, IonSpinner } from '@ionic/react';
import { arrowBackOutline, refreshOutline } from 'ionicons/icons';
import Button from '../../../../Button/Button';
import { useTranslate } from '../../../../../hooks/context/useTranslate';
import { useLayoutEditorContext } from '../../../../../hooks/context/useLayoutEditorContext';

interface EditorHeaderProps {
  onBack: () => void;
}

const EditorHeader: FC<EditorHeaderProps> = ({ onBack }) => {
  // Covers the whole save flow (PUT + the post-save reload) — isSaving from
  // the context spans only the PUT itself.
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { t } = useTranslate();
  const { definition, layout, isDirty, isSaving, saveLayout, reset } =
    useLayoutEditorContext();

  const isBusy = isSaving || isSubmitting;

  const handleSave = async (): Promise<void> => {
    if (isBusy) {
      return;
    }

    setIsSubmitting(true);
    try {
      await saveLayout();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="layout-editor-header">
      <div className="layout-editor-title">
        {/* The texts ellipsize on narrow windows — the tooltip carries the
            full value. */}
        <h2 title={definition?.caption ?? t('layoutEditor.title')}>
          {definition?.caption ?? t('layoutEditor.title')}
        </h2>
        <span title={layout?.name}>{layout?.name}</span>
      </div>
      <div className="layout-editor-actions">
        <Button
          onClick={onBack}
          fill="clear"
          round={false}
          title={t('layoutEditor.backHint')}
        >
          <IonIcon icon={arrowBackOutline} slot="start" />
          {t('layoutEditor.back')}
        </Button>
        <Button
          onClick={reset}
          fill="clear"
          round={false}
          disabled={!isDirty || isBusy}
          title={t('layoutEditor.resetHint')}
        >
          <IonIcon icon={refreshOutline} slot="start" />
          {t('layoutEditor.reset')}
        </Button>
        <Button
          onClick={handleSave}
          disabled={!isDirty || isBusy}
          title={t('layoutEditor.saveHint')}
        >
          {isBusy && (
            <IonSpinner
              className="layout-editor-save-spinner"
              name="crescent"
            />
          )}
          {t('save')}
        </Button>
      </div>
    </div>
  );
};

export default EditorHeader;
